/**
 * Binary steps, and binary names for them. BigQuery bills per tebibyte and the
 * ops settings hold both byte thresholds in gibibytes, so a decimal "TB" here
 * would disagree with the rate it is multiplied by — by about ten percent at
 * tebibyte scale, which is real money on a bill nobody would re-derive by hand.
 * The Slack threshold alert labels the same division "TiB", and one figure under
 * two names is a discrepancy an operator would go looking for.
 */
const BYTE_STEP = 1024;
const BYTE_UNITS = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"] as const;

/**
 * Byte counts run to tebibytes within a day, where a fully grouped number costs
 * more width than it carries meaning in a KPI strip, a table cell, or a chart
 * axis. One decimal is kept so two adjacent rows do not both read "3 TiB".
 * Surfaces that can be hovered pair this with `formatBytesExact` in a `title`.
 */
export function formatBytesCompact(bytes: number, locale: string): string {
  const abs = Math.abs(bytes);

  // Repeated division rather than a logarithm: `log(1024 ** 4) / log(1024)`
  // lands a hair under 4 in floating point and would report a tebibyte as
  // "1,024 GiB". Halving a binary value is exact.
  let step = 0;
  let scaled = abs;
  while (scaled >= BYTE_STEP && step < BYTE_UNITS.length - 1) {
    scaled /= BYTE_STEP;
    step += 1;
  }

  // A scaled value at or above 100 has three significant digits already, so the
  // decimal is noise; below that it is 1.2 TiB against 1.9 TiB.
  const maximumFractionDigits = scaled >= 100 ? 0 : 1;
  const signed = bytes < 0 ? -scaled : scaled;

  return `${signed.toLocaleString(locale, { maximumFractionDigits })} ${BYTE_UNITS[step]}`;
}

export function formatBytesExact(bytes: number, locale: string): string {
  return `${bytes.toLocaleString(locale)} B`;
}

/**
 * Share of the window, for the breakdown tables. Null when there is no total to
 * take a share of, so the caller renders nothing rather than "0%" or "NaN%" —
 * either of which claims a measurement the window cannot make.
 */
export function formatByteShare(bytes: number, total: number, locale: string): string | null {
  if (total <= 0 || bytes <= 0) return null;
  return `${((bytes / total) * 100).toLocaleString(locale, { maximumFractionDigits: 1 })}%`;
}
